/**
 * Sign-in around the panel (ADR 0012). The gate asks the API who is signed in; without a
 * session (or the Tailscale identity) it shows the login screen, with one it creates and
 * starts the panel store and renders the app. When the session ends while the panel is open
 * (a 401 from any request, or the live stream failing while GET /auth/session says signed
 * out) the store stops and the login screen comes back on the same address, so the route is
 * kept: after signing in again the same screen opens. Logout lives in the user menu.
 */

import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import type { AuthApi, AuthSession, SignedIn } from '../api/auth.js';
import type { DevTools } from '../screens/Diger.js';
import { Giris, type GirisNotice } from '../screens/Giris.js';
import { App } from './App.js';
import { AuthContext } from './auth-context.js';
import type { PanelStore } from './store.js';

/** Tells the gate that the session ended: the API layer calls expired() on a 401. */
export interface SessionWatch {
  expired(): void;
  /** Asks the server (at most every 10 s) and calls expired() when it says signed out. */
  probe(): void;
  subscribe(listener: () => void): () => void;
}

export function createSessionWatch(auth: Pick<AuthApi, 'session'>, now: () => number = () => Date.now()): SessionWatch {
  const listeners = new Set<() => void>();
  let lastProbe = Number.NEGATIVE_INFINITY;
  let probing = false;
  const watch: SessionWatch = {
    expired() {
      for (const listener of [...listeners]) listener();
    },
    probe() {
      if (probing || now() - lastProbe < 10_000) return;
      lastProbe = now();
      probing = true;
      auth
        .session()
        .then((session) => {
          if (!session.authenticated) watch.expired();
        })
        .catch(() => {
          // No answer: the stream is down for another reason; the panel shows it as stale.
        })
        .finally(() => {
          probing = false;
        });
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
  return watch;
}

/** A fetch that reports every 401 of the panel API to the watch (the session is gone). */
export function watchingFetch(
  watch: Pick<SessionWatch, 'expired'>,
  fetchImpl: (input: string, init: RequestInit) => Promise<Response> = (input, init) => fetch(input, init),
): (input: string, init: RequestInit) => Promise<Response> {
  return async (input, init) => {
    const response = await fetchImpl(input, init);
    if (response.status === 401) watch.expired();
    return response;
  };
}

type Phase =
  | { name: 'checking' }
  | { name: 'signed-out'; notice: GirisNotice }
  | { name: 'signed-in'; session: AuthSession; store: PanelStore };

export function AuthGate({
  auth,
  watch,
  createStore,
  devTools = null,
  children,
}: {
  auth: AuthApi;
  watch: SessionWatch;
  /** A new panel store (not started) for every sign-in. */
  createStore: () => PanelStore;
  devTools?: DevTools | null;
  /** Rendered instead of the App (tests). */
  children?: (store: PanelStore) => ReactNode;
}) {
  const [phase, setPhase] = useState<Phase>({ name: 'checking' });
  const phaseRef = useRef<Phase>(phase);
  phaseRef.current = phase;

  const signIn = useCallback(
    (session: AuthSession, recoveryCodesLeft: number | null = null) => {
      const store = createStore();
      store.start();
      if (recoveryCodesLeft !== null) {
        store.showToast(`Kurtarma kodu kullanıldı. ${recoveryCodesLeft} kod kaldı.`, { icon: 'info' });
      }
      setPhase({ name: 'signed-in', session, store });
    },
    [createStore],
  );

  const signOut = useCallback((notice: GirisNotice) => {
    const current = phaseRef.current;
    if (current.name === 'signed-in') current.store.stop();
    setPhase({ name: 'signed-out', notice });
  }, []);

  // Who is signed in, once at start.
  useEffect(() => {
    let cancelled = false;
    auth
      .session()
      .then((session) => {
        if (cancelled) return;
        if (session.authenticated) signIn(session);
        else setPhase({ name: 'signed-out', notice: null });
      })
      .catch(() => {
        if (!cancelled) setPhase({ name: 'signed-out', notice: 'offline' });
      });
    return () => {
      cancelled = true;
    };
  }, [auth, signIn]);

  // The session ended elsewhere (idle, absolute, logout on another device, a password change).
  useEffect(
    () =>
      watch.subscribe(() => {
        if (phaseRef.current.name === 'signed-in') signOut('expired');
      }),
    [watch, signOut],
  );

  // Leaving the page stops the store.
  useEffect(
    () => () => {
      const current = phaseRef.current;
      if (current.name === 'signed-in') current.store.stop();
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } finally {
      signOut('logged-out');
    }
  }, [auth, signOut]);

  if (phase.name === 'checking') {
    return (
      <main className="login login--checking" aria-busy="true">
        <p className="login__checking key">Twin · bağlanıyor…</p>
      </main>
    );
  }
  if (phase.name === 'signed-out') {
    return (
      <Giris
        auth={auth}
        notice={phase.notice}
        onSignedIn={(result: SignedIn) => signIn(result.session, result.recoveryCodesLeft)}
      />
    );
  }
  return (
    <AuthContext.Provider value={{ session: phase.session, logout }}>
      {children ? children(phase.store) : <App store={phase.store} devTools={devTools} />}
    </AuthContext.Provider>
  );
}
